import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ProductTableHeader,
  SortDirection,
  SortField,
} from "./ProductTableHeader";
import { ProductTableRow } from "./ProductTableRow";
import {
  CombinedWarehouseOrderItemDto,
  OrderLineItemDto,
} from "@lib/ShiOneClient";
import { OrderDrawerIdentifier } from "../../types";
import { useGetOrderLineRelatedData } from "@features/orders-features/hooks/useGetOrderLineRelatedData";
import { useGetOrderLineSerials } from "@features/orders-features/hooks/useGetOrderLineSerials";
import { LineItem } from "@features/orders-features/types/drawer";
import { useOrderTelemetrySection } from "../../orderTelemetrySection";

export type ProductRow = {
  lines: LineItem[];
  byStatus: Record<string, number>;
};

type ProductSortFields = {
  extendedPrice?: number | null;
  unitPrice?: number | null;
  quantity?: number | null;
  productName?: string | null;
  productDescriptor?: string | null;
};

type ProductRecord = ProductRow & {
  key: string;
};

export function buildProductRows(lines: LineItem[]): ProductRecord[] {
  const productRows = lines.reduce<Record<string, ProductRow>>((acc, line) => {
    const existingRow = acc[line.id];

    if (existingRow) {
      existingRow.lines.push(line);
    } else {
      acc[line.id] = {
        lines: [line],
        byStatus: {},
      };
    }

    const row = acc[line.id];

    const qtyCurrentByStatus = row.byStatus[line.status] ?? 0;

    const shippedQty = line.item.qtyShipped;
    const qty =
      shippedQty != null && shippedQty > 0
        ? shippedQty
        : (line.item.quantity ?? 0);

    row.byStatus[line.status] = qtyCurrentByStatus + qty;

    acc[line.id] = row;
    return acc;
  }, {});

  return Object.entries(productRows).map(([key, row]) => ({
    key,
    ...row,
    lines: [...row.lines].sort((left, right) => {
      const leftLineNumber = left.item.lineNumber ?? Number.MAX_SAFE_INTEGER;
      const rightLineNumber = right.item.lineNumber ?? Number.MAX_SAFE_INTEGER;
      return leftLineNumber - rightLineNumber;
    }),
  }));
}

export function getRepresentativeLine(lines: LineItem[]): LineItem {
  // Prefer the most recent shipped-like line; otherwise use the first captured line.
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    const line = lines[i];
    if (
      line.status.toLowerCase().startsWith("ship") ||
      (line.item.qtyShipped ?? 0) > 0
    ) {
      return line;
    }
  }

  return lines[0];
}

function getStatusLabels(row: ProductRow): string[] {
  return Object.keys(row.byStatus);
}

function getPrimaryLine(row: ProductRow): LineItem {
  return getRepresentativeLine(row.lines);
}

function formatStatusSummary(statusLabels: string[]) {
  return statusLabels
    .slice()
    .sort((left, right) => left.localeCompare(right))
    .join(", ");
}

const STATUS_SORT_RANKS: Record<string, number> = {
  processing: 1,
  orderplaced: 1,
  shipped: 2,
  invoiced: 3,
  delivered: 3,
};

export function getStatusProgressScore(status: string): number {
  return STATUS_SORT_RANKS[status.trim().toLowerCase()] ?? 0;
}

export function getRowStatusProgress(row: ProductRow): number {
  const labels = getStatusLabels(row);
  if (!labels.length) {
    return 0;
  }

  return labels.reduce((max, label) => {
    const score = getStatusProgressScore(label);
    return score > max ? score : max;
  }, 0);
}

export function compareStatusRows(
  left: ProductRow,
  right: ProductRow,
  sortDirection: SortDirection,
): number {
  const directionMultiplier = sortDirection === "asc" ? 1 : -1;
  const leftProgress = getRowStatusProgress(left);
  const rightProgress = getRowStatusProgress(right);

  if (leftProgress !== rightProgress) {
    // For ascending status sort, show furthest-along statuses first.
    return (rightProgress - leftProgress) * directionMultiplier;
  }

  return (
    formatStatusSummary(getStatusLabels(left)).localeCompare(
      formatStatusSummary(getStatusLabels(right)),
    ) * directionMultiplier
  );
}

function getSortFields(
  item: LineItem["item"],
): LineItem["item"] & ProductSortFields {
  return item as LineItem["item"] & ProductSortFields;
}

function compareProductRecords(
  left: ProductRecord,
  right: ProductRecord,
  sortField: SortField,
  sortDirection: SortDirection,
) {
  const directionMultiplier = sortDirection === "asc" ? 1 : -1;
  const leftItem = getSortFields(getPrimaryLine(left).item);
  const rightItem = getSortFields(getPrimaryLine(right).item);

  const leftTotal =
    leftItem.extendedPrice ??
    (leftItem.unitPrice ?? 0) * (leftItem.quantity ?? 0);
  const rightTotal =
    rightItem.extendedPrice ??
    (rightItem.unitPrice ?? 0) * (rightItem.quantity ?? 0);

  if (sortField === "product") {
    const leftName = (
      leftItem.productName ??
      leftItem.mfgPartNumber ??
      ""
    ).toLowerCase();
    const rightName = (
      rightItem.productName ??
      rightItem.mfgPartNumber ??
      ""
    ).toLowerCase();
    return leftName.localeCompare(rightName) * directionMultiplier;
  }

  if (sortField === "status") {
    return compareStatusRows(left, right, sortDirection);
  }

  if (sortField === "unitPrice") {
    return (
      ((leftItem.unitPrice ?? 0) - (rightItem.unitPrice ?? 0)) *
      directionMultiplier
    );
  }

  if (sortField === "quantity") {
    return (
      ((leftItem.quantity ?? 0) - (rightItem.quantity ?? 0)) *
      directionMultiplier
    );
  }

  return (leftTotal - rightTotal) * directionMultiplier;
}

export function ProductTable({
  orderId,
  items,
  serials,
  relatedData,
  myWarehouseOrders,
  currencyCode,
  formatPrice,
  onProductClick,
  canOpenProduct,
  isBackorder,
  showStatusColumn = true,
  showActionsColumn = true,
}: Readonly<{
  orderId: OrderDrawerIdentifier;
  items: LineItem[];
  serials?: ReturnType<typeof useGetOrderLineSerials>["data"];
  relatedData?: ReturnType<typeof useGetOrderLineRelatedData>["data"];
  myWarehouseOrders?: CombinedWarehouseOrderItemDto[] | null;
  currencyCode?: string | null;
  formatPrice?: (amount: number) => string;
  onProductClick?: (item: OrderLineItemDto) => void;
  canOpenProduct?: (item: OrderLineItemDto) => boolean;
  isBackorder: (
    productDescriptor: string | undefined,
    productClass: string | undefined,
  ) => boolean;
  showStatusColumn?: boolean;
  showActionsColumn?: boolean;
}>) {
  const [sortField, setSortField] = useState<SortField>(
    showStatusColumn ? "status" : "product",
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const telemetrySection = useOrderTelemetrySection();

  const db = useMemo<ProductRecord[]>(() => buildProductRows(items), [items]);

  const sortedItems = useMemo(() => {
    const sorted = [...db];
    sorted.sort((left, right) =>
      compareProductRecords(left, right, sortField, sortDirection),
    );
    return sorted;
  }, [db, sortDirection, sortField]);

  const onSortHeaderClick = (field: SortField) => {
    if (sortField !== field) {
      setSortField(field);
      setSortDirection("asc");
      return;
    }
    setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
  };

  const getSerials = useCallback(
    (lineNumber: number | undefined) => {
      if (!lineNumber || !serials) {
        return undefined;
      }
      return serials.filter(
        (p: NonNullable<typeof serials>[number]) =>
          !!p.lineNumber && p.lineNumber === lineNumber,
      );
    },
    [serials],
  );

  const getRelatedData = useCallback(
    (lineNumber: number | undefined) => {
      if (!lineNumber || !relatedData) {
        return undefined;
      }
      return relatedData.filter(
        (p: NonNullable<typeof relatedData>[number]) =>
          !!p.lineNumber && p.lineNumber === lineNumber,
      );
    },
    [relatedData],
  );

  const getWarehouseOrderLine = useCallback(
    (lineNumber: number | undefined) => {
      if (!lineNumber || !myWarehouseOrders?.length) {
        return undefined;
      }
      return myWarehouseOrders.filter(
        (p) => p.lineNum === lineNumber && p.uniqueIdentifier,
      );
    },
    [myWarehouseOrders],
  );

  if (!items.length) {
    return (
      <Box
        sx={{
          mx: 2,
          my: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 2, py: 2 }}>
          <Typography color="text.secondary" variant="body2">
            No products found.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <TableContainer>
        <Table
          size="small"
          aria-label={`${orderId.salesOrder}: Products`}
          sx={{ tableLayout: "fixed" }}
        >
          <ProductTableHeader
            sortField={sortField}
            sortDirection={sortDirection}
            onSortHeaderClick={onSortHeaderClick}
            telemetrySection={telemetrySection}
            showStatusColumn={showStatusColumn}
            showActionsColumn={showActionsColumn}
          />

          <TableBody>
            {sortedItems.map((row) => (
              <ProductTableRow
                key={row.key}
                row={row}
                getSerials={getSerials}
                getRelatedData={getRelatedData}
                getWarehouseOrderLineItems={getWarehouseOrderLine}
                currencyCode={currencyCode}
                formatPrice={formatPrice}
                onProductClick={onProductClick}
                canOpenProduct={canOpenProduct}
                isBackorder={isBackorder}
                showStatusColumn={showStatusColumn}
                showActionsColumn={showActionsColumn}
              />
            ))}
          </TableBody>

          <TableFooter>
            <TableRow>
              <TableCell
                colSpan={(showStatusColumn ? 5 : 4) + (showActionsColumn ? 1 : 0)}
                sx={{ px: 2, py: 1.5 }}
              >
                <Typography color="text.secondary" variant="body2">
                  {db.length} {db.length === 1 ? "item" : "items"}
                </Typography>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Card>
  );
}

export default ProductTable;
